import { LIST_TYPES, ListType } from 'constants/listTypes';
import { MessageMedium } from 'constants/messageMediums';
import uniq from 'lodash/uniq';
import {
  EmailDataType,
  GenericDataType,
  InAppDataType,
  PushDataType,
  SmsDataType,
} from 'modules/domains/segmentation/DataType';
import { ScheduledRecurringListTrigger } from 'modules/workflowNodeEditors/components/startNode/triggers/ScheduledRecurringListTrigger';
import { AND } from 'utils/combinatorTypes';
import { EMAIL_CAMPAIGN, LIST_MEMBERSHIP_SINGLE, MESSAGE_CHANNEL } from 'utils/fieldConstants';
import { Combinator } from '../types';
import { WORKFLOW_TRIGGERS } from './base';

export enum ResourceType {
  List = 'list',
  Campaign = 'campaign',
  Channel = 'channel',
  CustomEvent = 'customEvent',
  ProfileField = 'profileField',
  String = 'string',
  MessageType = 'messageType',
}

/**
 * @deprecated
 * Updated code should pull `MessageMedium` from `constants/messageMediums`
 */
export { MessageMedium } from 'constants/messageMediums';

export const API_TRIGGER_EVENT_NAME = 'apiTrigger';
export const DEFAULT_TRIGGER_EVENT_NAME = API_TRIGGER_EVENT_NAME;

export const STATIC_LIST_TYPES = [LIST_TYPES.SUPPRESSION, LIST_TYPES.STANDARD, LIST_TYPES.INTERNAL];

/**
 * TODO: Create an interface for each type of Trigger and set this to be oneof
 * ex: `type TriggerMetadata = SendTrigger | ListTrigger | etc...`
 * That way we can enforce the expected type for each trigger type
 */
export interface TriggerMetadata {
  /** dataType associated with the trigger.  Used in filters form. */
  dataType?: string;
  /** Message medium associated with campaign trigger. */
  messageMedium?: MessageMedium;
  /** Can filters be added to this trigger? */
  hasFilter: boolean;
  /** Friendly name shown in the trigger select dropdown */
  label: string;
  /** Help text if there are no resources available */
  noResourceHelpText?: string;
  /** The field name in nodeData associated with the resource */
  resourceFieldName?: string;
  /** The type of resource.  If not set, the resource will not show. */
  resourceType?: ResourceType | null;
  /** If resourceType, the type of resource value. */
  resourceValueType?: 'number' | 'string';
  /** If resourceType, whether the resource has link */
  resourceHasLink?: boolean;
  /**  If resourceType, the resource title */
  resourceTitle?: string;
  /** Placeholder for resource selector */
  selectPlaceholder?: string;
  /**
   * The corresponding event name for the trigger.
   * For most cases, this is the same of the dataType.  However, custom events
   * will either use a user-provided name or a name in the system.
   */
  triggerEventNames?: string | null;
  /** Any extra form data associated with a trigger */
  TriggerForm?: () => JSX.Element | null;
  /** Types of lists to be displayed as options */
  listType?: ListType[];
  /** Category Id for options */
  categoryId?: string;
  /** Suffix for options */
  Suffix?: string;
  /** Start tile refresh labels */
  // TODO: remove replace label with refreshLabel after StartTileRefresh GA
  refreshLabel?: string;
}

export interface TriggerMetadataMap {
  [key: string]: TriggerMetadata;
}

/**
 * Start node triggers in alphabetical order by label
 * Triggers excluded from beta are commented out
 */
export const TRIGGER_METADATA: TriggerMetadataMap = {
  [WORKFLOW_TRIGGERS.ADDED_ITEM_TO_CART]: {
    Suffix: 'System event',
    categoryId: 'frequent',
    /**
     * It looks like the dataType for this trigger is `customEvent`, but no one
     * seemed to be able to 100% confirm this.
     *
     * @see https://iterable.slack.com/archives/C02CQ0XL2HM/p1683585440923099
     * @see https://iterable.slack.com/archives/C9B8SQNQG/p1683659514897929
     */
    dataType: GenericDataType.CustomEvent,
    hasFilter: true,
    label: 'Shopping cart update',
    triggerEventNames: 'updateCart',
  },
  [WORKFLOW_TRIGGERS.CLICKED_AN_IN_APP]: {
    categoryId: 'messageMedium',
    dataType: InAppDataType.Click,
    hasFilter: true,
    label: 'In-App Click',
    messageMedium: MessageMedium.InApp,
    noResourceHelpText: 'There are no campaigns in this project',
    resourceFieldName: EMAIL_CAMPAIGN,
    resourceHasLink: false,
    resourceTitle: 'Specify campaign',
    resourceType: ResourceType.Campaign,
    resourceValueType: 'number',
    selectPlaceholder: 'Select a campaign...',
    triggerEventNames: InAppDataType.Click,
  },
  [WORKFLOW_TRIGGERS.CLOSED_AN_IN_APP]: {
    categoryId: 'messageMedium',
    dataType: InAppDataType.Close,
    hasFilter: true,
    label: 'In-App Close',
    messageMedium: MessageMedium.InApp,
    noResourceHelpText: 'There are no campaigns in this project',
    resourceFieldName: EMAIL_CAMPAIGN,
    resourceHasLink: false,
    resourceTitle: 'Specify campaign',
    resourceType: ResourceType.Campaign,
    resourceValueType: 'number',
    selectPlaceholder: 'Select a campaign...',
    triggerEventNames: InAppDataType.Close,
  },
  [WORKFLOW_TRIGGERS.DELETED_AN_IN_APP]: {
    categoryId: 'messageMedium',
    dataType: InAppDataType.Delete,
    hasFilter: true,
    label: 'In-App Delete',
    messageMedium: MessageMedium.InApp,
    noResourceHelpText: 'There are no campaigns in this project',
    resourceFieldName: EMAIL_CAMPAIGN,
    resourceHasLink: false,
    resourceTitle: 'Specify campaign',
    resourceType: ResourceType.Campaign,
    resourceValueType: 'number',
    selectPlaceholder: 'Select a campaign...',
    triggerEventNames: InAppDataType.Delete,
  },
  [WORKFLOW_TRIGGERS.DELIVERED_AN_IN_APP]: {
    categoryId: 'messageMedium',
    dataType: InAppDataType.Delivery,
    hasFilter: true,
    label: 'In-App Delivery',
    messageMedium: MessageMedium.InApp,
    noResourceHelpText: 'There are no campaigns in this project',
    resourceFieldName: EMAIL_CAMPAIGN,
    resourceHasLink: false,
    resourceTitle: 'Specify campaign',
    resourceType: ResourceType.Campaign,
    resourceValueType: 'number',
    selectPlaceholder: 'Select a campaign...',
    triggerEventNames: InAppDataType.Delivery,
  },
  [WORKFLOW_TRIGGERS.OPENED_AN_IN_APP]: {
    categoryId: 'messageMedium',
    dataType: InAppDataType.Open,
    hasFilter: true,
    label: 'In-App Open',
    messageMedium: MessageMedium.InApp,
    noResourceHelpText: 'There are no campaigns in this project',
    resourceFieldName: EMAIL_CAMPAIGN,
    resourceHasLink: false,
    resourceTitle: 'Specify campaign',
    resourceType: ResourceType.Campaign,
    resourceValueType: 'number',
    selectPlaceholder: 'Select a campaign...',
    triggerEventNames: InAppDataType.Open,
  },
  [WORKFLOW_TRIGGERS.OPENED_PUSH]: {
    categoryId: 'messageMedium',
    dataType: PushDataType.Open,
    hasFilter: true,
    label: 'Push Open',
    messageMedium: MessageMedium.Push,
    noResourceHelpText: 'There are no push campaigns in this project',
    resourceFieldName: EMAIL_CAMPAIGN,
    resourceHasLink: false,
    resourceTitle: 'Specify campaign',
    resourceType: ResourceType.Campaign,
    resourceValueType: 'number',
    selectPlaceholder: 'Select a campaign...',
    triggerEventNames: PushDataType.Open,
  },
  [WORKFLOW_TRIGGERS.CLICKED_AN_EMAIL]: {
    categoryId: 'messageMedium',
    dataType: EmailDataType.Click,
    hasFilter: true,
    label: 'Email Click',
    messageMedium: MessageMedium.Email,
    resourceFieldName: EMAIL_CAMPAIGN,
    resourceHasLink: false,
    resourceTitle: 'Specify campaign',
    resourceType: ResourceType.Campaign,
    resourceValueType: 'number',
    selectPlaceholder: 'Select a campaign...',
    triggerEventNames: EmailDataType.Click,
  },
  [WORKFLOW_TRIGGERS.OPENED_AN_EMAIL]: {
    categoryId: 'messageMedium',
    dataType: EmailDataType.Open,
    hasFilter: true,
    label: 'Email Open',
    messageMedium: MessageMedium.Email,
    resourceFieldName: EMAIL_CAMPAIGN,
    resourceHasLink: false,
    resourceTitle: 'Specify campaign',
    resourceType: ResourceType.Campaign,
    resourceValueType: 'number',
    selectPlaceholder: 'Select a campaign...',
    triggerEventNames: EmailDataType.Open,
  },
  [WORKFLOW_TRIGGERS.SEND_AN_EMAIL]: {
    categoryId: 'messageMedium',
    dataType: EmailDataType.Send,
    hasFilter: true,
    label: 'Email Send',
    messageMedium: MessageMedium.Email,
    resourceFieldName: EMAIL_CAMPAIGN,
    resourceHasLink: false,
    resourceTitle: 'Specify campaign',
    resourceType: ResourceType.Campaign,
    resourceValueType: 'number',
    selectPlaceholder: 'Select a campaign...',
    triggerEventNames: EmailDataType.Send,
  },
  [WORKFLOW_TRIGGERS.PURCHASED]: {
    Suffix: 'System event',
    categoryId: 'frequent',
    dataType: GenericDataType.Purchase,
    hasFilter: true,
    label: 'Purchase',
    triggerEventNames: GenericDataType.Purchase,
  },
  [WORKFLOW_TRIGGERS.CLICKED_A_WHATSAPP]: {
    categoryId: 'messageMedium',
    dataType: 'whatsAppClick',
    hasFilter: true,
    label: 'WhatsApp Click',
    triggerEventNames: 'whatsAppClick',
  },
  [WORKFLOW_TRIGGERS.RECEIVED_A_WHATSAPP]: {
    categoryId: 'messageMedium',
    dataType: 'whatsAppReceived',
    hasFilter: true,
    label: 'WhatsApp Reply',
    triggerEventNames: 'whatsAppReceived',
  },
  [WORKFLOW_TRIGGERS.RECEIVED_AN_SMS]: {
    categoryId: 'messageMedium',
    dataType: SmsDataType.Received,
    hasFilter: true,
    label: 'SMS Reply',
    triggerEventNames: SmsDataType.Received,
  },
  [WORKFLOW_TRIGGERS.CLICKED_AN_SMS]: {
    categoryId: 'messageMedium',
    dataType: SmsDataType.Click,
    hasFilter: true,
    label: 'SMS Click',
    triggerEventNames: SmsDataType.Click,
  },
  [WORKFLOW_TRIGGERS.RECEIVED_API]: {
    dataType: GenericDataType.CustomEvent,
    hasFilter: true,
    label: 'API call',
    triggerEventNames: API_TRIGGER_EVENT_NAME,
  },
  [WORKFLOW_TRIGGERS.SCHEDULED_RECURRING_LIST]: {
    TriggerForm: ScheduledRecurringListTrigger,
    hasFilter: false,
    label: 'Schedule',
    listType: [LIST_TYPES.DYNAMIC, LIST_TYPES.STANDARD],
    noResourceHelpText: 'There are no recurring lists in this project',
    resourceFieldName: LIST_MEMBERSHIP_SINGLE,
    resourceHasLink: true,
    resourceTitle: 'Specify list',
    resourceType: ResourceType.List,
    resourceValueType: 'number',
    selectPlaceholder: 'Select a list...',
    triggerEventNames: API_TRIGGER_EVENT_NAME,
  },
  [WORKFLOW_TRIGGERS.SUBSCRIBED_TO_CHANNEL]: {
    categoryId: 'membership',
    dataType: GenericDataType.Subscribe,
    hasFilter: false,
    label: 'Channel subscribe',
    noResourceHelpText: 'There are no channels in this project',
    // TODO: remove replace label with refreshLabel after StartTileRefresh GA
    refreshLabel: 'Subscribe to channel',
    resourceFieldName: MESSAGE_CHANNEL,
    resourceHasLink: false,
    resourceTitle: 'Specify channel',
    resourceType: ResourceType.Channel,
    resourceValueType: 'number',
    selectPlaceholder: 'Select a channel...',
    triggerEventNames: GenericDataType.Subscribe,
  },
  [WORKFLOW_TRIGGERS.SUBSCRIBED_TO_LIST]: {
    categoryId: 'membership',
    dataType: GenericDataType.Subscribe,
    hasFilter: false,
    label: 'Subscribe',
    listType: STATIC_LIST_TYPES,
    noResourceHelpText: 'There are no static lists in this project',
    // TODO: remove replace label with refreshLabel after StartTileRefresh GA
    refreshLabel: 'Subscribe to list',
    resourceFieldName: LIST_MEMBERSHIP_SINGLE,
    resourceHasLink: true,
    resourceTitle: 'Specify list',
    resourceType: ResourceType.List,
    resourceValueType: 'number',
    selectPlaceholder: 'Select a list...',
    triggerEventNames: GenericDataType.Subscribe,
  },
  [WORKFLOW_TRIGGERS.SUBSCRIBED_TO_MESSAGE_TYPE]: {
    categoryId: 'membership',
    hasFilter: false,
    label: 'Message type subscribe',
    // TODO: Figure out why this made the hold tile not work properly dataType: GenericDataType.Subscribe,
    // TODO: remove replace label with refreshLabel after StartTileRefresh GA
    refreshLabel: 'Subscribe to message type',
    resourceFieldName: 'messageTypeId',
    resourceHasLink: false,
    resourceTitle: 'Specify message type',
    resourceType: ResourceType.MessageType,
    resourceValueType: 'number',
    selectPlaceholder: 'Select a message type...',
    triggerEventNames: GenericDataType.Subscribe,
  },
  [WORKFLOW_TRIGGERS.TRIGGERED_BY_WORKFLOW]: {
    hasFilter: false,
    label: 'Other journey',
    triggerEventNames: 'triggeredByWorkflow',
  },
  [WORKFLOW_TRIGGERS.TRIGGERED_CUSTOM_EVENT]: {
    categoryId: 'frequent',
    dataType: GenericDataType.CustomEvent,
    hasFilter: true,
    label: 'Custom Event',
    noResourceHelpText: 'There are no custom events in this project',
    resourceFieldName: 'eventName',
    resourceHasLink: false,
    resourceTitle: 'Specify event',
    resourceType: ResourceType.CustomEvent,
    resourceValueType: 'string',
    selectPlaceholder: 'Enter custom event name...',
    triggerEventNames: null, // actual value should be custom event added by user
  },
  [WORKFLOW_TRIGGERS.UNSUBSCRIBED_FROM_CHANNEL]: {
    categoryId: 'membership',
    dataType: GenericDataType.UnSubscribe,
    hasFilter: false,
    label: 'Channel unsubscribe',
    noResourceHelpText: 'There are no channels in this project',
    // TODO: remove replace label with refreshLabel after StartTileRefresh GA
    refreshLabel: 'Unsubscribe from channel',
    resourceFieldName: MESSAGE_CHANNEL,
    resourceHasLink: false,
    resourceTitle: 'Specify channel',
    resourceType: ResourceType.Channel,
    resourceValueType: 'number',
    selectPlaceholder: 'Select a channel...',
    triggerEventNames: GenericDataType.UnSubscribe,
  },
  [WORKFLOW_TRIGGERS.UNSUBSCRIBED_FROM_LIST]: {
    categoryId: 'membership',
    dataType: GenericDataType.UnSubscribe,
    hasFilter: false,
    label: 'Unsubscribe',
    listType: STATIC_LIST_TYPES,
    noResourceHelpText: 'There are no static lists in this project',
    // TODO: remove replace label with refreshLabel after StartTileRefresh GA
    refreshLabel: 'Unsubscribe from list',
    resourceFieldName: LIST_MEMBERSHIP_SINGLE,
    resourceHasLink: true,
    resourceTitle: 'Specify list',
    resourceType: ResourceType.List,
    resourceValueType: 'number',
    selectPlaceholder: 'Select a list...',
    triggerEventNames: GenericDataType.UnSubscribe,
  },
  [WORKFLOW_TRIGGERS.UNSUBSCRIBED_FROM_MESSAGE_TYPE]: {
    categoryId: 'membership',
    hasFilter: false,
    label: 'Message type unsubscribe',
    // TODO: Figure out why this made the hold tile not work properly dataType: GenericDataType.UnSubscribe,
    // TODO: remove replace label with refreshLabel after StartTileRefresh GA
    refreshLabel: 'Unsubscribe from message type',
    resourceFieldName: 'messageTypeId',
    resourceHasLink: false,
    resourceTitle: 'Specify message type',
    resourceType: ResourceType.MessageType,
    resourceValueType: 'number',
    selectPlaceholder: 'Select a message type...',
    triggerEventNames: GenericDataType.UnSubscribe,
  },
  [WORKFLOW_TRIGGERS.FIELD_CHANGED]: {
    categoryId: 'frequent',
    dataType: GenericDataType.User,
    hasFilter: true,
    label: 'User profile update',
    resourceFieldName: 'field',
    resourceHasLink: false,
    resourceTitle: 'Specify field',
    resourceType: ResourceType.ProfileField,
    resourceValueType: 'string',
    selectPlaceholder: 'Select a field...',
    triggerEventNames: 'fieldChange',
  },
  [WORKFLOW_TRIGGERS.NEW_USER_CREATED]: {
    categoryId: 'frequent',
    dataType: GenericDataType.User,
    hasFilter: true,
    label: 'New user created',
    triggerEventNames: 'fieldChange',
  },
};

export const RESOURCE_FIELD_NAMES = uniq(
  Object.values(TRIGGER_METADATA)
    .map(({ resourceFieldName }) => resourceFieldName)
    .filter((n) => n !== null && n !== undefined),
);

const DEFAULT_SEARCH_QUERY = {
  dataType: GenericDataType.CustomEvent,
  castType: null,
  comparatorType: null,
  dynamicValue: null,
  field: null,
  fieldType: null,
  value: null,
};

/**
 * Minimum data required to save start node containing selected trigger
 * with filter turned on
 */
export const DEFAULT_SEARCH_COMBO = {
  searchQueries: [DEFAULT_SEARCH_QUERY],
  combinator: AND as Combinator,
};
